import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { dataGridPremiumColumns } from '../../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  AddButtonUi,
  DatagridPremiumUi,
  InputUI,
  DatePickerUI,
  SelectUI,
} from '../../Interface';
import dayjs from 'dayjs';
import { Box, Tooltip } from '@mui/material';
import { D3Status } from '../../../config/complaintReport';

function EditToolbar(props) {
  const { disabled, push, defAction } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newData = {
      ...defAction,
      id: uuidv4(),
    };

    await push(newData);

    const fieldsToValidate = [
      `action.${values.action.length ?? 0}.name`,
      `action.${values.action.length ?? 0}.responsiblePerson`,
      `action.${values.action.length ?? 0}.date`,
      `action.${values.action.length ?? 0}.status`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
}

const ComplaintReportD3 = ({ disabled, defAction, setNoteDialogState }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.action.findIndex((rc) => rc.id === id);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Intézkedés',
        type: 'string',
        editable: true,
        renderCell: (params) => {
          const value = params?.row?.name ?? '';
          return (
            <Tooltip title={value}>
              <Box
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {value}
              </Box>
            </Tooltip>
          );
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`action.${valueIndex}.name`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.action?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`action.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`action.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'date',
        headerName: 'Dátum',
        type: 'date',
        editable: true,
        valueGetter: (_data, row) => {
          return row.date ? new Date(row.date) : '';
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`action.${valueIndex}.date`}
              component={DatePickerUI}
              helperText={<FormErrorUI message={errors?.action?.[valueIndex]?.date} />}
              variant="standard"
              value={dayjs(data.row.date)}
              onChange={(newDate) => {
                const finalDate = newDate ? newDate.startOf('day').add(12, 'hour').toDate() : null;

                setFieldValue(`action.${valueIndex}.date`, finalDate);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: finalDate,
                });
              }}
              onBlur={async () => await validateField(`action.${valueIndex}.date`)}
            />
          );
        },
      },
      {
        field: 'responsiblePerson',
        headerName: 'Felelős',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.action?.[valueIndex]?.responsiblePerson;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`action.${valueIndex}.responsiblePerson`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.action?.[valueIndex]?.responsiblePerson} />}
              onChange={(e) => {
                setFieldValue(`action.${valueIndex}.responsiblePerson`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`action.${valueIndex}.responsiblePerson`)}
            />
          );
        },
      },
      {
        field: 'status',
        headerName: 'Állapot',
        type: 'string',
        editable: true,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return values.action?.[valueIndex]?.status;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`action.${valueIndex}.status`}
              type="text"
              variant="standard"
              component={SelectUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.action?.[valueIndex]?.status} />}
              option={D3Status}
              onChange={(e) => {
                setFieldValue(`action.${valueIndex}.status`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              value={values.action?.[valueIndex]?.status ?? ''}
              onBlur={async () => await validateField(`action.${valueIndex}.status`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
          <FieldArray name="action">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.ComplaintReportAction}
                height={400}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.action ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defAction,
                  },
                }}
                onCellDoubleClick={(params, event) => {
                  event.defaultMuiPrevented = true;
                  if (disabled) return;
                  if (params.field === 'name') {
                    event.defaultMuiPrevented = true;
                    const valueIndex = getValueIndex(values, params.row.id);
                    setNoteDialogState({ open: true, fieldName: `action.${valueIndex}.name` });
                    return;
                  }
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default ComplaintReportD3;
